/**
 * Paddle payment links (`?_ptxn=txn_...`).
 *
 * Paddle mails these for invoices, failed-payment recovery and card updates,
 * and points them at the seller's default payment link — this app. The
 * transaction already exists in Paddle, carrying its own customer and its own
 * custom_data, so completing one needs no Lark session, no app_admin role and
 * no checkout token from us. The link *is* the credential.
 *
 * That is why this runs BEFORE the Lark gate. Someone clicking the link in a
 * desktop mail client has no Lark webview, and answering a payment request
 * with "Open in Lark" strands a payment we asked for.
 *
 * Paddle.js would also open the overlay by itself, spotting the parameter
 * during Initialize(). We deliberately do not rely on that: the parameter is
 * stripped before Paddle ever loads and the transaction is opened explicitly,
 * so there is exactly one code path and re-opening after the customer closes
 * the overlay is the same call as opening it the first time.
 *
 * Initialize() previously only ran on the Billing page, behind an app-admin
 * check, so a link landing on the dashboard opened nothing at all. It appeared
 * to work inside Lark only because hash routing keeps the query string for the
 * whole session: navigating to Billing later would belatedly pick it up.
 */
import { ref, computed, onScopeDispose } from 'vue'
import { usePaddle, onPaddleEvent } from '@/composables/usePaddle'

/** Paddle's own query parameter. Named by Paddle, not by us. */
const PADDLE_TXN_PARAM = '_ptxn'

export type PaymentLinkState = 'opening' | 'open' | 'closed' | 'completed' | 'error'

/**
 * Read the transaction id once, at module load, and take it out of the URL
 * immediately.
 *
 * Immediately, because Paddle.js opens the overlay itself if it finds the
 * parameter at Initialize() time — which would race the explicit open below.
 * Stripping also stops a reload or a shared URL reopening a checkout for a
 * transaction that has already been settled. `replaceState` leaves no history
 * entry to go Back into.
 */
const paymentTransactionId = (() => {
  const url = new URL(window.location.href)
  const id = url.searchParams.get(PADDLE_TXN_PARAM)
  if (id) {
    url.searchParams.delete(PADDLE_TXN_PARAM)
    // Preserves history.state: vue-router keeps its own bookkeeping there, and
    // replacing it with null makes it warn and lose scroll restoration.
    window.history.replaceState(window.history.state, '', url.toString())
  }
  return id
})()

/** Re-attach the transaction to a URL — see App.vue's Lark deep link. */
export function withPaymentLink(url: URL): URL {
  if (paymentTransactionId) url.searchParams.set(PADDLE_TXN_PARAM, paymentTransactionId)
  return url
}

export function usePaymentLink() {
  /** Whether this page load arrived from a Paddle payment link. */
  const isPaymentLink = computed(() => !!paymentTransactionId)

  const state = ref<PaymentLinkState>('opening')

  const { openTransaction } = usePaddle()

  let unsubscribe: (() => void) | null = null

  /**
   * Load Paddle.js and open the linked transaction.
   *
   * Safe to call when there is no payment link — it returns immediately, so
   * the caller needs no guard of its own.
   */
  async function open(): Promise<void> {
    if (!paymentTransactionId) return

    state.value = 'opening'

    unsubscribe ??= onPaddleEvent((event) => {
      switch (event.name) {
        case 'checkout.loaded':
          state.value = 'open'
          break
        case 'checkout.completed':
          state.value = 'completed'
          break
        case 'checkout.closed':
          // Dismissed without paying. Without this the page would sit on
          // "complete your payment in the checkout window" pointing at a
          // window that is no longer there, and the parameter is already
          // stripped so a reload would not bring it back either.
          if (state.value !== 'completed') state.value = 'closed'
          break
        case 'checkout.error':
          // Already paid, voided or expired. Paddle's payload holds nothing a
          // customer could act on, so this stays a generic message.
          state.value = 'error'
          break
      }
    })

    try {
      await openTransaction(paymentTransactionId)
    } catch (error) {
      console.error('[PaymentLink] Failed to open Paddle checkout:', error)
      state.value = 'error'
    }
  }

  onScopeDispose(() => {
    unsubscribe?.()
  })

  return { isPaymentLink, state, open }
}
